import { faroCoreApiClient } from "@api/faro-core-api-client";
import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { FaroTextButton } from "@components/common/faro-text-button";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import {
  BaseCompanyIdProps,
  BaseProjectIdProps,
} from "@custom-types/base-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { useToast } from "@hooks/use-toast";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { tableTypeSelector } from "@store/table/table-selector";
import {
  removeOneAfterFetching,
  removeOneManually,
  setBulkActionName,
} from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { isMemberActive } from "@utils/user-utils";
import { useMemo } from "react";

interface Props extends BaseCompanyIdProps, Partial<BaseProjectIdProps> {
  /** The entities that are selected */
  selectedEntities: SdbMemberTypes[];

  /** The context of the table */
  subjectType: MemberTableSubject;
}

/**
 * Resend invitation in bulk to the unregistered members of:
 * projects when project is available and component rendered under the team tab of project overview,
 */
export function BulkResendInvitation({
  selectedEntities,
  companyId,
  projectId,
  subjectType,
}: Props): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const tableType = useAppSelector(tableTypeSelector);

  /** Returns appropriate tooltip text for resend invitation button */
  const tooltipText = useMemo(() => {
    switch (subjectType) {
      case "workspace":
      case "group":
      case "team":
        return "Resend invitation to workspace";

      case "project":
        return "Resend invitation to project";

      default:
        return undefined;
    }
  }, [subjectType]);

  async function onResendInvitationClick(): Promise<void> {
    const bulkActionResults: { success: number; failed: number } = {
      success: 0,
      failed: 0,
    };

    if (tableType) {
      trackEvent({
        name: BulkActionEvents.bulkResendInvitation,
        props: {
          dataType: tableType,
          numberOfEntities: selectedEntities.length,
        },
      });
    }

    dispatch(setBulkActionName("resendInvitation"));

    /** Collects all promises to resend invitations so that we can wait for all of them only once */
    const resendInvitationPromises = selectedEntities.map((member) => {
      if (isMemberActive(member)) {
        dispatch(removeOneManually(member.identity));
        return "skip";
      }
      if (projectId && subjectType === "project") {
        // No need to be an asyncThunk as we don't need to update the store
        return faroCoreApiClient.projects
          .resendProjectMembershipInvite({
            companyId,
            projectId,
            email: member.email,
          })
          .then(() => {
            dispatch(removeOneAfterFetching(member.identity));
          });
      } else if (
        subjectType === "workspace" ||
        subjectType === "group" ||
        subjectType === "team"
      ) {
        // No need to be an asyncThunk as we don't need to update the store
        return faroCoreApiClient.company
          .resendCompanyMembershipInvite({
            companyId,
            userIdentity: member.email,
          })
          .then(() => {
            dispatch(removeOneAfterFetching(member.identity));
          });
      }
      return undefined;
    });

    await Promise.allSettled(resendInvitationPromises).then((results) => {
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value !== "skip") {
          bulkActionResults.success++;
        } else if (result.status === "rejected") {
          bulkActionResults.failed++;
          const error = result.reason;
          showToast({
            message: getErrorDisplayMarkup(error),
            type: "error",
          });
        }
      });
    });

    if (bulkActionResults.failed === 0) {
      showToast({
        message: "Invitations sent",
        type: "success",
      });
    } else if (bulkActionResults.success > 0) {
      showToast({
        message: `${bulkActionResults.success} of ${
          bulkActionResults.success + bulkActionResults.failed
        } invitations sent`,
        type: "info",
      });
    }

    dispatch(setBulkActionName(null));
  }

  return (
    <FaroTextButton
      onClick={onResendInvitationClick}
      tooltipText={tooltipText}
      sx={{ fontWeight: 600 }}
    >
      Resend invitation
    </FaroTextButton>
  );
}
